/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Typography } from "../..";
import PropTypes from 'prop-types';
import { forwardRef, Fragment, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { bgColor, border, borderColor, borderRadius, boxBorder, cursorNotAllowed, cursorPointer, displayBlock, displayFlex, flexRow, itemsCenter, itemsStart, left, mg, mgb, parseMinWidthHeight, parseWidth, parseWidthHeight, positionAbsolute, positionRelative, scale, textColor, top } from "../../../styles/general";
import { useTheme } from "../../../theme";
import { classNames, randomString, refType as ref } from "../../../utils";
const {
  colors: {
    system: {
      white: systemWhite
    },
    fill: {
      hover: fillHover,
      pressed: fillPressed
    }
  },
  spacing
} = useTheme();
const padding = 0.75;
const Checkbox = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  checked,
  children,
  className,
  color,
  defaultChecked,
  determinate,
  disabled,
  forTreeView,
  id,
  inputProps,
  inputRef,
  label,
  labelProps,
  name,
  onChange,
  readOnly,
  required,
  stopPropagation,
  value,
  width,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  if (!inputRef) inputRef = useRef(null);
  const ref = useRef(null);
  if (typeof width === 'number') width += 'px';
  const [checkedState, setCheckedState] = useState(checked || defaultChecked);
  if (!id) {
    [id] = useState('DGN-UI-' + randomString(10, {
      allowNumber: false,
      allowSymbol: false
    }));
  }
  const _CheckBoxSquareCSS = CheckBoxSquareCSS(width, color);
  const _CheckBoxInputCSS = CheckBoxInputCSS(_CheckBoxSquareCSS.name, color);
  const handleChange = event => {
    stopPropagation && event.stopPropagation();
    if (readOnly || inputProps.readOnly || disabled || inputProps.disabled) return;
    const oldLogicChecked = inputRef.current.checked; //Use for TreeView
    if (forTreeView) {
      inputRef.current.checked = !oldLogicChecked; //Use for TreeView
    }

    setChecked(checked !== undefined ? Boolean(checked) : !checkedState);
    const currentInputRef = inputRef.current;
    if (!forTreeView) inputRef.current.checked = !checkedState;
    const e = {
      value: forTreeView ? oldLogicChecked : !checkedState,
      target: currentInputRef
    };
    if (onChange) onChange(e, checkedState);
  };
  const setChecked = check => {
    if (typeof check === 'boolean') {
      setCheckedState(check);
    }
  };
  useEffect(() => {
    if (checked !== undefined) setCheckedState(Boolean(checked));
  }, [checked]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      determinate: determinate,
      setChecked: setChecked,
      getChecked: () => checkedState,
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    currentRef.determinate = determinate;
    currentRef.setChecked = setChecked;
    currentRef.getChecked = () => checkedState;
    return currentRef;
  });
  return jsx("div", {
    id: 'DGN-UI-Checkbox-' + id.replace('DGN-UI-', ''),
    css: CheckboxContainerCSS,
    className: classNames('DGN-UI-Checkbox', className),
    ref: ref,
    ...props,
    onClick: () => {
      var _inputRef$current$cli, _inputRef$current;
      return (_inputRef$current$cli = (_inputRef$current = inputRef.current).click) === null || _inputRef$current$cli === void 0 ? void 0 : _inputRef$current$cli.call(_inputRef$current);
    }
  }, jsx("div", {
    css: CheckBoxRootCSS,
    className: classNames('DGN-UI-Checkbox-Root', disabled && 'disabled', readOnly && 'read-only')
  }, jsx("input", {
    value: value,
    ...inputProps,
    className: classNames(determinate ? 'determinate' : 'indeterminate', inputProps === null || inputProps === void 0 ? void 0 : inputProps.className),
    readOnly: readOnly || inputProps.readOnly,
    disabled: disabled || inputProps.disabled,
    ref: inputRef,
    css: _CheckBoxInputCSS,
    id: id,
    name: name,
    type: "checkbox",
    checked: checkedState,
    onChange: handleChange,
    hidden: true
  }), jsx("label", {
    css: CheckBoxLabelCSS
  }, jsx("span", {
    className: classNames(determinate ? 'determinate' : 'indeterminate', readOnly && 'read-only', disabled && 'disabled'),
    css: _CheckBoxSquareCSS
  }), children || label ? jsx(Typography, {
    type: 'p1',
    style: {
      marginLeft: spacing(1)
    },
    color: disabled ? 'system.disabled' : '',
    ...labelProps
  }, jsx(Fragment, null, children || label, required ? jsx("span", {
    css: textColor(disabled ? 'system.disabled' : 'semantic.danger')
  }, "*") : null)) : null)));
}));
const CheckBoxSquareCSS = (width, color) => css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${cursorPointer};
    ${boxBorder};
    ${parseWidthHeight(width)};
    ${border(2, 'system.rest')};
    ${parseMinWidthHeight(width)};
    ${bgColor('white')};
    ${borderRadius(2)};
    ${mg([padding])};
    &.determinate {
        &::after {
            ${displayBlock};
            ${positionAbsolute};
            ${parseWidthHeight(12.6, 7)};
            ${left('50%')};
            ${top('43%')};
            content: '';
            transform: translate(-50%, -50%) scale(0) rotate(45deg);
            transition: transform 0.3s;
        }
    }
    &.indeterminate {
        &::after {
            ${displayFlex};
            ${flexRow};
            ${positionAbsolute};
            ${parseWidthHeight(10, 2)};
            ${bgColor('white')};
            ${left(2)};
            ${top(6)};
            ${scale(0)};
            content: '';
            transition: transform 0.3s;
        }
    }
    &:not(.disabled):not(.read-only) {
        &:hover {
            box-shadow: 0 0 0 3px ${fillHover};
            ${bgColor('fill.hover')};
            ${borderColor(color)};
        }
        &:focus,
        &:active {
            box-shadow: 0 0 0 3px ${fillPressed};
            ${bgColor('fill.pressed')};
        }
    }
    &:not(disabled):focus {
        box-shadow: 0 0 0 3px ${fillPressed};
        ${bgColor('fill.pressed')};
    }
    &.disabled,
    &.read-only {
        ${cursorNotAllowed};
    }
`;
const CheckBoxInputCSS = (CheckBoxSquareCSSName, color) => css`
    &:checked + label > span.css-${CheckBoxSquareCSSName} {
        &.determinate {
            &::after {
                transform: translate(-50%, -66%) scale(1) rotate(-45deg);
                background: linear-gradient(${systemWhite}, ${systemWhite}) left bottom/2px 100%,
                    linear-gradient(${systemWhite}, ${systemWhite}) left bottom/100% 2px;
                background-repeat: no-repeat;
            }
        }
        &.indeterminate {
            &::after {
                ${scale(1)};
            }
        }
    }
    &:checked:not(:disabled) + label > span.css-${CheckBoxSquareCSSName} {
        ${bgColor(color)};
        ${borderColor(color)};
    }
    &:checked:disabled {
        & + label > span.css-${CheckBoxSquareCSSName} {
            &:not(.indeterminate) {
                ${bgColor('system.disabled')};
            }
            &.indeterminate {
                ${bgColor('formal2')};
                ${borderColor('formal2')};
            }
        }
    }
    &:disabled {
        & + label > span.css-${CheckBoxSquareCSSName} {
            ${borderColor('system.disabled')};
        }
    }
`;
const CheckBoxLabelCSS = css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${itemsCenter};
    ${boxBorder};
`;
const CheckBoxRootCSS = css`
    ${displayFlex};
    ${flexRow};
    ${itemsStart};
    ${boxBorder};
    & > label {
        ${mgb(0)};
    }
`;
const CheckboxContainerCSS = css`
    ${displayBlock};
    ${positionRelative};
    ${parseWidth('fit-content')};
`;
Checkbox.defaultProps = {
  className: '',
  color: 'primary',
  defaultChecked: false,
  determinate: true,
  disabled: false,
  inputProps: {},
  label: '',
  labelProps: {},
  readOnly: false,
  stopPropagation: true,
  width: 18
};
Checkbox.propTypes = {
  /** If `true`, the component is checked. */
  checked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([0, 1])]),
  /** Class for component. */
  className: PropTypes.string,
  /** The main color of the component. ['primary', 'info', 'success', 'warning', 'danger', #hex, rgb(#, #, #)] */
  color: PropTypes.string,
  /** The default checked state. Use when the component is not controlled. */
  defaultChecked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([0, 1])]),
  /** If `true` display tick or indeterminate if `false`. */
  determinate: PropTypes.bool,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** The id of the component. */
  id: PropTypes.string,
  /** [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element. */
  inputProps: PropTypes.object,
  /** Pass a ref to the input element. */
  inputRef: ref,
  /** The name of the Radio is displayed on the interface. */
  label: PropTypes.string,
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/typography--title) applied to the `Typography` label. */
  labelProps: PropTypes.object,
  /** The name of the Checkbox corresponds to the label displayed on the interface. */
  name: PropTypes.string,
  /**
   * Callback fired when the state is changed.
   *
   * You can pull out the new checked state by accessing `event.value` or `event.target.checked` (boolean).
   */
  onChange: PropTypes.func,
  /** If `true`, the component is readonly. */
  readOnly: PropTypes.bool,
  /** If `true`, the label will indicate that the checkbox is required. */
  required: PropTypes.bool,
  /** The value of input */
  value: PropTypes.any,
  /** The width of checkbox. */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
export default Checkbox;